import { Component, ElementRef, HostListener, Inject, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

/** One image of the gallery — a message's attachment. */
export interface ChatImageViewerItem {
  /** Message id (clientMessageId while pending) — what the opener starts on. */
  id: string;
  url: string;
  name?: string;
}

export interface ChatImageViewerData {
  images: ChatImageViewerItem[];
  index: number;
}

/** Zoom levels, as multiples of the fitted size. */
const ZOOM_STEPS: ReadonlyArray<number> = [1, 1.5, 2, 3];

/** The zoom a click on the image jumps to. */
const CLICK_ZOOM_STEP = 2;

/** Stage padding on each side — the stylesheet's `padding: 1rem`, kept clear when fitting. */
const STAGE_INSET_PX = 16;

/**
 * Image viewer (DEV-2062) — what a chat image thumbnail opens instead of a new tab.
 *
 * One full-viewport dialog: the backdrop paints the darkness, the surface is
 * transparent, and the image is sized in pixels from its natural size — fitted to
 * the stage, times the zoom — so a zoomed image pans with the stage's own scroll.
 * ←/→ and the side buttons move through the gallery; +/−/0 and the toolbar zoom;
 * Esc, ✕ and a click on the stage close. Scrolling under the dialog is blocked by
 * MatDialog's default scroll strategy. Opened through `openChatImageViewer`.
 */
@Component({
  selector: 'chat-image-viewer-dialog',
  templateUrl: './chat-image-viewer-dialog.component.html',
  styleUrls: ['./chat-image-viewer-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatImageViewerDialogComponent {

  readonly images: ChatImageViewerItem[];

  index: number;

  /** Index into `ZOOM_STEPS`. */
  zoomStep = 0;

  loading = true;

  failed = false;

  /** Rendered size in px, set once the image loads; null keeps the stylesheet's fallback. */
  width: number | null = null;

  height: number | null = null;

  @ViewChild('stage', { static: true })
  private readonly stage!: ElementRef<HTMLElement>;

  private naturalWidth = 0;

  private naturalHeight = 0;

  /** The press that a stage `click` ends with started on the stage itself, not on the image. */
  private pressedOnStage = false;

  constructor(
    private readonly dialogRef: MatDialogRef<ChatImageViewerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: ChatImageViewerData,
  ) {
    this.images = data.images;
    this.index = Math.min(Math.max(data.index, 0), this.images.length - 1);
    this.preloadNeighbours();
  }

  get current(): ChatImageViewerItem {
    return this.images[this.index];
  }

  get hasPrevious(): boolean {
    return this.index > 0;
  }

  get hasNext(): boolean {
    return this.index < this.images.length - 1;
  }

  get zoom(): number {
    return ZOOM_STEPS[this.zoomStep];
  }

  get zoomLabel(): string {
    return `${Math.round(this.zoom * 100)}%`;
  }

  get canZoomIn(): boolean {
    return !this.failed && this.zoomStep < ZOOM_STEPS.length - 1;
  }

  get canZoomOut(): boolean {
    return this.zoomStep > 0;
  }

  // ---------------------------------------------------------------------------
  // Image
  // ---------------------------------------------------------------------------

  onLoad(event: Event): void {
    const image = event.target as HTMLImageElement;
    this.naturalWidth = image.naturalWidth;
    this.naturalHeight = image.naturalHeight;
    this.loading = false;
    this.failed = false;
    this.fit();
  }

  onError(): void {
    this.loading = false;
    this.failed = true;
  }

  /** A smaller window refits; a bigger one gives the image its room back. */
  @HostListener('window:resize')
  onWindowResize(): void {
    this.fit();
  }

  // ---------------------------------------------------------------------------
  // Gallery
  // ---------------------------------------------------------------------------

  previous(): void {
    if (this.hasPrevious) this.show(this.index - 1);
  }

  next(): void {
    if (this.hasNext) this.show(this.index + 1);
  }

  // ---------------------------------------------------------------------------
  // Zoom
  // ---------------------------------------------------------------------------

  zoomIn(): void {
    if (this.canZoomIn) this.setZoom(this.zoomStep + 1);
  }

  zoomOut(): void {
    if (this.canZoomOut) this.setZoom(this.zoomStep - 1);
  }

  resetZoom(): void {
    this.setZoom(0);
  }

  /** The image itself toggles between fitted and the click zoom. */
  toggleZoom(): void {
    if (this.failed) return;
    this.setZoom(this.zoomStep === 0 ? CLICK_ZOOM_STEP : 0);
  }

  // ---------------------------------------------------------------------------
  // Closing
  // ---------------------------------------------------------------------------

  close(): void {
    this.dialogRef.close();
  }

  onStagePointerDown(event: PointerEvent): void {
    this.pressedOnStage = event.target === event.currentTarget;
  }

  /**
   * Outside the image = on the stage. A press that started on the image and was
   * released on the stage (a failed drag) is not "clicking outside".
   */
  onStageClick(event: MouseEvent): void {
    if (event.target === event.currentTarget && this.pressedOnStage) this.close();
  }

  /** Esc is MatDialog's; the rest of the keyboard lives here. */
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.defaultPrevented) return;
    switch (event.key) {
      case 'ArrowLeft':
        this.previous();
        break;
      case 'ArrowRight':
        this.next();
        break;
      case '+':
      case '=':
        this.zoomIn();
        break;
      case '-':
        this.zoomOut();
        break;
      case '0':
        this.resetZoom();
        break;
      default:
        return;
    }
    event.preventDefault();
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  private show(index: number): void {
    this.index = index;
    this.zoomStep = 0;
    this.loading = true;
    this.failed = false;
    this.width = null;
    this.height = null;
    this.naturalWidth = 0;
    this.naturalHeight = 0;
    this.preloadNeighbours();
  }

  private setZoom(step: number): void {
    this.zoomStep = step;
    this.fit();
    this.centerStage();
  }

  /** Natural size, shrunk to the stage (never enlarged), times the zoom. */
  private fit(): void {
    if (!this.naturalWidth || !this.naturalHeight) return;
    const stage = this.stage.nativeElement;
    const maxWidth = Math.max(stage.clientWidth - 2 * STAGE_INSET_PX, 1);
    const maxHeight = Math.max(stage.clientHeight - 2 * STAGE_INSET_PX, 1);
    const scale = Math.min(maxWidth / this.naturalWidth, maxHeight / this.naturalHeight, 1) * this.zoom;
    this.width = Math.round(this.naturalWidth * scale);
    this.height = Math.round(this.naturalHeight * scale);
  }

  /** After a zoom the stage scrolls to the middle of the image, not its top-left corner. */
  private centerStage(): void {
    requestAnimationFrame(() => {
      const stage = this.stage.nativeElement;
      stage.scrollLeft = (stage.scrollWidth - stage.clientWidth) / 2;
      stage.scrollTop = (stage.scrollHeight - stage.clientHeight) / 2;
    });
  }

  /** Warms the cache of the neighbours, so stepping through the gallery does not wait. */
  private preloadNeighbours(): void {
    [this.images[this.index - 1], this.images[this.index + 1]]
      .filter((image): image is ChatImageViewerItem => !!image)
      .forEach((image) => {
        new Image().src = image.url;
      });
  }
}
